import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import { Box, Button, Grid } from '@mui/material'

import { classToFComponent, FCClassContext } from 'src/utils/views'
import ExportDocumentsDialog from 'src/components/exportdocuments/export_documents_dialog'
import { GroupProfileScope, HistoryQueryFilter } from '../gp_scopes'
import ChartConsumoFranquiaView from './components/chart_consumo_franquia'
import CrmApiKeysSection from './components/crm-api-keys-section'
import CrmConnectionsSection from './components/crm-connections-section'
import CrmExportRequestsSection from './components/crm-export-requests-section'
import GroupProfileFilters from './components/filters'
import GroupUsersTable from './components/group_users_table'
import GroupProfileHistoryTable from './components/history_table'
import LimitChangedHistoryTable from './components/limit_change_history_table'
import GroupProfile from './components/profile'

const LOG = Logger.get('UserProfile-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    },
    min600Height: {
        minHeight: 600
    }
})

type GroupProfileViewProps = {
    className?: string
    scope: GroupProfileScope
}

class GroupProfileClass implements FCClassContext<GroupProfileViewProps> {
    // :: emissors
    readonly emitFind = (data: Partial<HistoryQueryFilter>) => {
        const filter = { ...this.scope.filterScope.filters, ...data }
        this.scope.filterScope.onFind(filter).catch(LOG.caught)
    }

    readonly emitGoBack = () => {
        this.scope.onGoBack().catch(LOG.caught)
    }

    // :: Fields
    scope!: GroupProfileScope
    sytles!: ReturnType<typeof useStyles>
    nameFilterInputRef!: React.RefObject<HTMLInputElement | null>

    // :: Methods

    onSyncState({ scope }: GroupProfileViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: GroupProfileViewProps) {
        this.scope = bindUpdate(React, scope)
        this.nameFilterInputRef = React.useRef<HTMLInputElement>(null)
        const classes = this.sytles.classes

        if (scope.profileScope.waiting) {
            return <ViewSlot scope={scope.profileScope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'baseline',
                        gap: '10px'
                    }}
                >
                    <Grid
                        container
                        sx={{
                            mb: 1
                        }}
                    >
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <GroupProfile scope={scope.profileScope} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <GroupProfileFilters filterScope={scope.filterScope} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <ChartConsumoFranquiaView className={classes.min600Height} scope={scope.chartScope} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <GroupProfileHistoryTable scope={scope.historyScope} filterScope={scope.filterScope} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <GroupUsersTable scope={scope.profileScope} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <LimitChangedHistoryTable scope={scope.profileScope} />
                        </Grid>
                        {scope.canManageCrm && (
                            <>
                                <Grid size={{ xs: 12, sm: 12 }}>
                                    <CrmConnectionsSection scope={scope.crmConnectionsScope} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12 }}>
                                    <CrmApiKeysSection scope={scope.crmApiKeysScope} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12 }}>
                                    <CrmExportRequestsSection scope={scope.crmExportRequestsScope} />
                                </Grid>
                            </>
                        )}
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <Box sx={{ paddingLeft: 4, paddingRight: 4, marginBottom: 4 }}>
                                <Button variant="outlined" onClick={this.emitGoBack}>
                                    Voltar
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <ExportDocumentsDialog scope={scope.exportDocsScope} />
            </div>
        )
    }
}

export const GroupProfileView = classToFComponent(GroupProfileClass, React)
export default GroupProfileView
